/* --------------- *** RTE mark symbols *** --------------- */

(() => {
  "use strict";

  /**
   * Wraps legal-mark characters in <sup class="rte-mark rte-mark--{id}"> when they
   * appear as plain text inside `.inline-richtext`. Longer strings match before shorter ones.
   */
  class RteMarkSymbols {
    
  static TARGETS = [
    {
      id: "trademark",
      strings: ["\u2122"],
    },
    {
      id: "registered",
      strings: ["\u00AE"],
    },
    {
      id: "copyright",
      strings: ["\u00A9"],
    },
  ];

  static ROOT_SELECTOR = ".inline-richtext";

  static SKIP_SELECTOR =
    "script, style, noscript, textarea, code, pre, sup.rte-mark";

  /* --------------- *** Constructor *** --------------- */

  constructor() {
    this._boundHandleDomReady = this.handleDomReady.bind(this);
    this._boundHandleSectionLoad = this.handleSectionLoad.bind(this);
    this._matcher = RteMarkSymbols.buildMarkMatcher(RteMarkSymbols.TARGETS);
  }

  /* --------------- *** Lifecycle *** --------------- */

  /**
   * Registers document listeners. Safe to call once at theme boot (deferred script).
   */
  connect() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", this._boundHandleDomReady);
    } else {
      this.handleDomReady();
    }

    document.addEventListener("shopify:section:load", this._boundHandleSectionLoad);
  }

  /* --------------- *** Event handlers *** --------------- */

  handleDomReady() {
    this.run(document);
  }

  handleSectionLoad(event) {
    if (event.target) {
      this.run(event.target);
    }
  }

  /* --------------- *** Run / process *** --------------- */

  /**
   * Processes all `.inline-richtext` roots under `root` (or `document` when `root`
   * has no `querySelectorAll`).
   */
  run(root = document) {
    if (!this._matcher) {
      return;
    }

    const scope = root.querySelectorAll ? root : document;
    const rteRoots = scope.querySelectorAll(RteMarkSymbols.ROOT_SELECTOR);

    rteRoots.forEach((el) => this.processRteRoot(el));
  }

  processRteRoot(rteRoot) {
    const textNodes = this.collectTextNodes(rteRoot);

    for (const textNode of textNodes) {
      this.splitTextNode(textNode);
    }
  }

  /* --------------- *** Tree walk & text split *** --------------- */

  shouldSkipTextNode(textNode) {
    const parent = textNode.parentElement;

    if (!parent) {
      return true;
    }

    if (parent.closest(RteMarkSymbols.SKIP_SELECTOR)) {
      return true;
    }

    return false;
  }

  collectTextNodes(root) {
    const nodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

    let current = walker.nextNode();

    while (current) {
      if (!this.shouldSkipTextNode(current)) {
        nodes.push(current);
      }

      current = walker.nextNode();
    }

    return nodes;
  }

  splitTextNode(textNode) {
    const { re, matchToId } = this._matcher;
    const text = textNode.nodeValue;

    if (!text) {
      return;
    }

    const matches = [...text.matchAll(re)];

    if (!matches.length) {
      return;
    }

    const parent = textNode.parentNode;

    if (!parent) {
      return;
    }

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    for (const m of matches) {
      const index = m.index;
      const matched = m[0];

      if (typeof index !== "number") {
        continue;
      }

      if (index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, index)));
      }

      const sup = document.createElement("sup");
      const kind = matchToId.get(matched) || "mark";

      sup.className = `rte-mark rte-mark--${kind}`;
      sup.textContent = matched;
      fragment.appendChild(sup);
      lastIndex = index + matched.length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    parent.replaceChild(fragment, textNode);
  }

  /* --------------- *** Matcher *** --------------- */

  static escapeRegExp(string) {
    return string.replace(/[\\^$.*+?()[\]{}|]/g, "\\$&");
  }

  /**
   * Builds a Unicode-aware regex and a map from matched substring to mark id.
   * Returns null when there are no strings to match.
   */
  static buildMarkMatcher(targets) {
    const pairs = [];

    for (const { id, strings } of targets) {
      for (const str of strings) {
        if (!str) continue;

        pairs.push({ str, id });
      }
    }

    pairs.sort((a, b) => b.str.length - a.str.length);

    if (!pairs.length) {
      return null;
    }

    const re = new RegExp(
      `(${pairs.map((p) => RteMarkSymbols.escapeRegExp(p.str)).join("|")})`,
      "gu",
    );
    const matchToId = new Map();

    for (const { str, id } of pairs) {
      if (!matchToId.has(str)) {
        matchToId.set(str, id);
      }
    }

    return { re, matchToId };
  }
  }

  /* --------------- *** Boot *** --------------- */

  const rteMarkSymbols = new RteMarkSymbols();

  rteMarkSymbols.connect();
})();
